import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { useMutation } from '@apollo/client';
import { Text, Button, Notification } from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { useAccountContext } from 'contexts/account';
import { StyledModal } from '../../payment-methods.style';
import { GET_ACCOUNT_PAYMENT_METHODS } from '../../payment-methods.api';
import { DELETE_PAYMENT_METHOD } from './delete-payment-method.api';

const getCopy = copyFor('settings.paymentInformation');
const { paymentWallet } = routes();

interface Props {
  close: () => void;
  paymentMethodId: number;
  paymentMethodInUse: boolean;
}

export const DeletePaymentMethod = ({
  close,
  paymentMethodId,
  paymentMethodInUse,
}: Props) => {
  const history = useHistory();
  const { addNotification } = useContext(Notification.Context);
  const { selectedAccountId } = useAccountContext();
  const { title, subTitle, subTitleAlt, buttons, notification } = getCopy(
    'deletePaymentMethod'
  );
  const refetchQueries = [
    {
      query: GET_ACCOUNT_PAYMENT_METHODS,
      variables: { accountId: selectedAccountId },
    },
  ];
  const [mutate] = useMutation(DELETE_PAYMENT_METHOD, {
    variables: { id: paymentMethodId },
    awaitRefetchQueries: true,
    refetchQueries,
  });

  const [{ error, loading }, removePaymentMethod] = useAsyncFn(async () => {
    await mutate();
    addNotification({
      displayTime: NOTIFICATION_DISPLAY_TIME,
      message: notification,
      onAdd: () => history.push(paymentWallet),
      variant: 'success',
    });
  }, [mutate]);

  return (
    <StyledModal aria-label={title} size="small">
      <StyledModal.Header title={title} />
      <StyledModal.Content>
        <Text margin={{ bottom: '32px' }}>
          {paymentMethodInUse ? subTitleAlt : subTitle}
        </Text>
        {error && (
          <Text margin={{ bottom: '32px' }}>
            {copyFor('errors.server')('generic')}
          </Text>
        )}
        {!paymentMethodInUse && (
          <Button
            loading={loading}
            backgroundColor="errorDark"
            fullWidth
            margin={{ bottom: '16px' }}
            onClick={() => removePaymentMethod()}
          >
            {buttons.remove}
          </Button>
        )}
        <Button backgroundless fullWidth onClick={close} disabled={loading}>
          {paymentMethodInUse ? buttons.close : buttons.cancel}
        </Button>
      </StyledModal.Content>
    </StyledModal>
  );
};
